import { ReactNode, forwardRef } from "react"
import clsx from "clsx"
import { Link } from "@inertiajs/react"

type Props = {
  size?: "sm" | "md" | "lg" | "xl"
  style?: "primary" | "secondary" | "tertiary" | "destructive"
  staticWidthClassName?: string
  fullWidth?: boolean
  loading?: boolean
  disabled?: boolean
  className?: string
  children: ReactNode
  onClick?: () => void
  href?: string
  [rest: string]: any
  asAnchorTag?: boolean
}

type Ref = HTMLButtonElement | HTMLAnchorElement

const Button = forwardRef<Ref, Props>(function Button(
  {
    size = "md",
    style = "primary",
    staticWidthClassName,
    fullWidth = false,
    loading = false,
    disabled = false,
    className,
    children,
    onClick,
    href,
    asAnchorTag = false,
    ...rest
  },
  ref
) {
  const spinner = (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
      <div
        className="h-4 w-4 animate-spin rounded-full border-2 border-current"
        style={{ borderRightColor: "transparent" }}
      />
    </div>
  )

  const isDisabled = disabled || loading

  const classes = clsx(
    "focus-visible:outline-chessdrop-500 relative inline-flex shrink-0 items-center justify-center border font-semibold focus-visible:outline-2 focus-visible:outline-offset-2",
    {
      "border-chessdrop-500 bg-chessdrop-500 text-white shadow-xs":
        style === "primary",
      "border-gray-200 bg-white text-gray-900 shadow-xs": style === "secondary",
      "border-transparent bg-transparent text-gray-900": style === "tertiary",
      "border-red-500 bg-white text-red-500 shadow-xs": style === "destructive",
      "hover:bg-chessdrop-600": style === "primary" && !isDisabled,
      "hover:border-gray-400": style === "secondary" && !isDisabled,
      "hover:bg-gray-200": style === "tertiary" && !isDisabled,
      "hover:bg-red-600 hover:text-white":
        style === "destructive" && !isDisabled,
      "opacity-50": isDisabled,
      "rounded-xl": ["sm", "md", "lg"].includes(size),
      "rounded-2xl": size === "xl",
      "px-4": size === "sm" && !staticWidthClassName,
      "px-5": size === "md" && !staticWidthClassName,
      "px-6": size === "lg" && !staticWidthClassName,
      "px-7": size === "xl" && !staticWidthClassName,
      "h-9 text-sm": size === "sm",
      "h-10 text-sm": size === "md",
      "h-12 text-base": size === "lg",
      "h-14 text-base": size === "xl",
      "w-full": fullWidth,
    },
    staticWidthClassName,
    className
  )

  const content = (
    <>
      <span
        className={clsx("flex items-center", {
          "gap-x-1": ["sm", "md"].includes(size),
          "gap-x-1.5": size === "lg",
          "opacity-0": loading,
        })}
      >
        {children}
      </span>
      {loading && spinner}
    </>
  )

  if (href) {
    if (disabled) {
      return (
        <button
          className={classes}
          disabled={isDisabled}
          ref={ref as React.Ref<HTMLButtonElement>}
          {...rest}
        >
          {content}
        </button>
      )
    }

    if (asAnchorTag || !href.startsWith("/")) {
      return (
        <a
          href={href}
          className={classes}
          ref={ref as React.Ref<HTMLAnchorElement>}
          onClick={onClick}
          {...rest}
        >
          {content}
        </a>
      )
    }

    return (
      <Link
        className={classes}
        href={href}
        ref={ref as React.Ref<HTMLAnchorElement>}
        onClick={onClick}
        {...rest}
      >
        {content}
      </Link>
    )
  }

  return (
    <button
      onClick={onClick}
      className={classes}
      disabled={isDisabled}
      ref={ref as React.Ref<HTMLButtonElement>}
      {...rest}
    >
      {content}
    </button>
  )
})

export default Button
